import React, { useMemo, useState } from 'react';
import backgroundImage from '@/assets/rate-bg.png';
import { BadgeRating } from '@/components/Badge/Badge';
import { getRatingColor, getVerdict, Verdict } from '@/helpers/ratingState';
import { APP_COLORS } from '@/config/colors';
import { Modal } from '@/components/Modal/Modal';
import { Button } from '@/components/Button/Button';
import { RatingStars } from '@/components/RatingStars';


export interface CardRatingProps {
  rating: number | null;
  maxRating?: number;
  expertWouldBuy: boolean | null;
  /** Nazwa firmowa rzeczoznawcy (np. "Motocontroler"). Wyświetlana po słowie "Rzeczoznawcę". */
  companyName?: string | null;
}

export const CardRating: React.FC<CardRatingProps> = ({
  rating,
  maxRating = 5,
  expertWouldBuy,
  companyName,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Bezpieczna wartość ratingu (domyślnie 0 gdy null)
  const safeRating = rating ?? 0;
  const verdict: Verdict = useMemo(() => {
    return getVerdict(safeRating);
  }, [safeRating]);

  const gradientFromVerdict = useMemo(() => {
    switch (verdict) {
      case Verdict.POSITIVE:
        return `linear-gradient(225.37deg, ${APP_COLORS.green[700]} 16.58%, ${APP_COLORS.green[200]} 94.73%)`;
      case Verdict.NEGATIVE:
        return `linear-gradient(225.37deg, ${APP_COLORS.red[800]} 16.58%, ${APP_COLORS.red[200]} 94.73%)`;
      case Verdict.NEUTRAL:
        return `linear-gradient(225.37deg, ${APP_COLORS.yellowDark} 16.58%, ${APP_COLORS.yellow} 94.73%)`;
      default:
        return '0';
    }
  }, [verdict]);

  const ratingColor = useMemo(
    () => getRatingColor(Math.floor(safeRating)),
    [safeRating],
  );

  return (
    // <div className="relative -left-[21px] -top-[54px] z-[2] flex items-center gap-2.5 rounded-3xl bg-white px-5 py-6 shadow-2xl">
    <div
      className="my-auto w-full min-w-60 flex-1 shrink basis-0 self-stretch overflow-hidden rounded-[15px] bg-cover bg-center"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <div className="relative flex min-h-[266px] w-full flex-col items-start px-6 py-6">
        <div className="z-0 flex w-[228px] max-w-full flex-col font-bold">
          <div className="text-3xl leading-none text-zinc-800">
            Ocena pojazdu
          </div>
          <BadgeRating rating={safeRating} version='v1' />
        </div>
        <div className="z-0 mt-10 flex w-[264px] max-w-full flex-col">
          <div className="flex gap-2 items-start self-start">
            <div className="gap-2.5 self-stretch whitespace-nowrap pt-2 text-7xl font-extrabold leading-none tracking-tighter text-zinc-800">
              {safeRating.toFixed(1)}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-2xl text-zinc-800">/</span>
              <span className="text-2xl text-zinc-800">{maxRating}</span>
            </div>
          </div>
          <div className="flex gap-4 items-center mt-4 w-full">
            <RatingStars
              value={safeRating}
              max={maxRating}
              ariaLabel="Ocena pojazdu"
              readOnly
              size="lg"
              activeColor={ratingColor}
              inactiveColor={APP_COLORS.gray[300]}
            />
          </div>
        </div>
      </div>
      <div
        className={`flex flex-col pt-4 pr-3 pb-2 pl-6 w-full text-white rounded-none`}
        style={{
          background: gradientFromVerdict,
        }}
      >
        <div className="flex gap-6 items-center w-full font-bold">
          <div className="flex flex-col flex-1 self-stretch my-auto shrink basis-0">
            <div className="text-lg leading-6">
              Czy rzeczoznawca kupiłby ten pojazd?
            </div>
            <div className="mt-3.5 gap-2.5 self-start whitespace-nowrap rounded-md bg-zinc-800 px-6 py-2 text-base uppercase leading-none tracking-wide">
              {expertWouldBuy === null ? 'Brak danych' : expertWouldBuy ? 'Tak' : 'Nie'}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className="mt-3 flex w-[169px] max-w-full items-center gap-1.5 self-end text-xs leading-none tracking-normal cursor-pointer hover:opacity-80 transition-opacity"
          aria-label="Zobacz szczegóły oceny"
        >
          <span className="text-right grow">Co składa się na tę opinię</span>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
            <path fillRule="evenodd" clipRule="evenodd" d="M8 16C10.1217 16 12.1566 15.1571 13.6569 13.6569C15.1571 12.1566 16 10.1217 16 8C16 5.87827 15.1571 3.84344 13.6569 2.34315C12.1566 0.842855 10.1217 0 8 0C5.87827 0 3.84344 0.842855 2.34315 2.34315C0.842855 3.84344 0 5.87827 0 8C0 10.1217 0.842855 12.1566 2.34315 13.6569C3.84344 15.1571 5.87827 16 8 16ZM4.927 4.99C4.64233 5.41933 4.5 5.84267 4.5 6.26C4.5 6.46333 4.59 6.652 4.77 6.826C4.95 7 5.17033 7.08667 5.431 7.086C5.87433 7.086 6.17533 6.83733 6.334 6.34C6.502 5.86467 6.70733 5.50467 6.95 5.26C7.19267 5.016 7.57067 4.894 8.084 4.894C8.52267 4.894 8.881 5.015 9.159 5.257C9.43633 5.49967 9.575 5.797 9.575 6.149C9.57621 6.32559 9.52919 6.49917 9.439 6.651C9.34635 6.80514 9.23333 6.94608 9.103 7.07C8.89273 7.26256 8.67663 7.44864 8.455 7.628C8.115 7.91 7.84433 8.15333 7.643 8.358C7.443 8.56333 7.282 8.801 7.16 9.071C6.838 10.316 8.51 10.416 8.896 9.527C8.94267 9.44167 9.01367 9.347 9.109 9.243C9.205 9.13967 9.33233 9.01967 9.491 8.883C9.8956 8.54603 10.2937 8.2013 10.685 7.849C10.9063 7.645 11.0973 7.40167 11.258 7.119C11.424 6.81806 11.5075 6.47859 11.5 6.135C11.5 5.66033 11.3587 5.22033 11.076 4.815C10.794 4.409 10.394 4.08833 9.876 3.853C9.358 3.61767 8.76067 3.5 8.084 3.5C7.356 3.5 6.719 3.641 6.173 3.923C5.627 4.205 5.21167 4.56133 4.927 4.99ZM7.067 12.07C7.067 12.3352 7.17236 12.5896 7.35989 12.7771C7.54743 12.9646 7.80178 13.07 8.067 13.07C8.33222 13.07 8.58657 12.9646 8.77411 12.7771C8.96164 12.5896 9.067 12.3352 9.067 12.07C9.067 11.8048 8.96164 11.5504 8.77411 11.3629C8.58657 11.1754 8.33222 11.07 8.067 11.07C7.80178 11.07 7.54743 11.1754 7.35989 11.3629C7.17236 11.5504 7.067 11.8048 7.067 12.07Z" fill="white" />
          </svg>
        </button>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Co składa się na tę opinię"
        size={950}
        hideClose={true}
      >
        <button
          onClick={() => setIsModalOpen(false)}
          className="absolute right-[-40px] top-[-40px] rounded-full lg:p-5 p-3 text-base bg-[#CA9A39]  focus:ring-[#CA9A39] text-white w-[80px] h-[80px]"
        >
          <svg width="37" height="37" viewBox="0 0 37 37" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="7.71582" y="10.789" width="4.36151" height="26.1691" transform="rotate(-45 7.71582 10.789)" fill="white" />
            <rect x="10.7939" y="29.2978" width="4.36151" height="26.1691" transform="rotate(-135 10.7939 29.2978)" fill="white" />
          </svg>

        </button>
        <div className="flex flex-col gap-4">
          <p className="text-sm leading-relaxed text-gray-600">
            Ocena pojazdu przedstawiona przez Rzeczoznawcę{companyName ? ` ${companyName}` : ''} jest subiektywna i wynika z jego doświadczenia, fachowej wiedzy oraz przeprowadzonych oględzin. Należy pamiętać, że ostateczna decyzja zakupowa należy wyłącznie do Kupującego po zapoznaniu się z pełną treścią raportu jak również dokumentacją zdjęciową.
          </p>
          <div>

            <Button
              variant="gold"
              size="small"
              onClick={() => setIsModalOpen(false)}
              className="inline-flex mt-2 lg:py-[11px] lg:pl-[26px] lg:pr-[8px]"
            >
              Rozumiem
              <svg className='ml-4' width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24ZM5.76083 13.3995L9.68704 17.282L19.2008 7.87369L17.551 6.24196L9.68704 14.0187L7.41067 11.7676L5.76083 13.3995Z" fill="white" />
              </svg>

            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
